import React, { useState, useEffect } from 'react';
import { X, Save, Wrench } from 'lucide-react';
import axios from 'axios';

const ServiceModal = ({
  isOpen,
  onClose,
  onConfirm,
  equipmentItem = null,
  serviceCompanies = [],
  mode = 'send' // 'send' or 'return'
}) => {
  // Handle both single item and array of items
  const equipmentItems = Array.isArray(equipmentItem) ? equipmentItem : (equipmentItem ? [equipmentItem] : []);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAvantIPDF, setShowAvantIPDF] = useState(false);
  const [pendingServiceData, setPendingServiceData] = useState(null);

  const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy'}/api`;

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === 'return' && equipmentItems.length > 0) {
        // For return mode, populate with current service data
        const firstItem = equipmentItems[0];
        setSelectedCompany(firstItem.service_company || '');
        setServiceDate(firstItem.service_date || getTodayDate());
        setNotes(firstItem.service_notes || '');
      } else {
        // For send mode, clear fields
        setSelectedCompany('');
        setServiceDate(getTodayDate());
        setNotes('');
      }
    }
  }, [isOpen, mode]); // Remove equipmentItems from dependencies to prevent re-render loop

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'return') {
      // Return mode: clear service data and change status to available
      const serviceData = {
        equipment_id: equipmentItems[0]?.id, // For backward compatibility
        equipment_ids: equipmentItems.map(item => item.id), // For multiple items
        service_company_id: null,
        service_date: null,
        notes: null,
        status: 'available'
      };

      setLoading(true);
      try {
        await onConfirm(serviceData);
        onClose();
      } catch (error) {
        console.error('Error returning equipment from service:', error);
        alert('Failed to return equipment from service');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Send mode validation
    if (!selectedCompany) {
      alert('Please select a service company');
      return;
    }

    const serviceData = {
      equipment_id: equipmentItems[0]?.id, // For backward compatibility
      equipment_ids: equipmentItems.map(item => item.id), // For multiple items
      service_company_id: selectedCompany,
      service_date: serviceDate,
      notes: notes,
      status: 'service'
    };

    // Check if selected company is Avanti
    const selectedCompanyObj = serviceCompanies.find(c => c.id === parseInt(selectedCompany));
    if (selectedCompanyObj && selectedCompanyObj.name.toLowerCase().includes('avanti')) {
      // Show Avanti PDF template for verification
      setPendingServiceData(serviceData);
      setShowAvantIPDF(true);
      return;
    }

    // For other companies, proceed normally
    setLoading(true);
    try {
      await onConfirm(serviceData);
      onClose();
    } catch (error) {
      console.error('Error sending equipment to service:', error);
      alert('Failed to send equipment to service');
    } finally {
      setLoading(false);
    }
  };

  const handleAvantiConfirm = async () => {
    if (!pendingServiceData) return;
    
    setLoading(true);
    try {
      // Download the PDF with proper authorization
      const equipmentIds = equipmentItems.map(item => item.id).join(',');
      const pdfUrl = `${API_BASE_URL}/avanti-service-template/${equipmentIds}/`;
      console.log('DEBUG: Downloading PDF for equipment IDs:', equipmentIds);
      console.log('DEBUG: PDF URL:', pdfUrl);
      console.log('DEBUG: API_BASE_URL:', API_BASE_URL);
      console.log('DEBUG: equipmentItems:', equipmentItems);
      
      // Use fetch with authorization token
      const response = await fetch(pdfUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      // Get the PDF blob
      const pdfBlob = await response.blob();
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = `avanti_service_template_${equipmentIds.replace(/,/g, '_')}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(downloadUrl);

      // IMPORTANT: Save service data to backend (same as other companies)
      // This updates SafetyEquipment with service_company, service_date, service_notes
      console.log('DEBUG: Saving service data to backend:', pendingServiceData);
      await onConfirm(pendingServiceData);

      setShowAvantIPDF(false);
      setPendingServiceData(null);
      onClose();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const errorMessage = error.message || 'Failed to download PDF';
      alert(`Failed to download PDF: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAvantiCancel = () => {
    setShowAvantIPDF(false);
    setPendingServiceData(null);
  };

  if (!isOpen && !showAvantIPDF) return null;

  // Avanti PDF Preview Modal
  if (showAvantIPDF) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleAvantiCancel} />
          
          <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={handleAvantiCancel}
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <Wrench className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                  Avanti Service Template
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Please review the Avanti service template before downloading it.
                </p>
                
                {/* PDF Viewer */}
                <div className="border rounded-lg h-96 bg-gray-50 flex items-center justify-center mb-4">
                  {(() => {
                    const previewUrl = `${API_BASE_URL}/avanti-service-template/${equipmentItems.map(item => item.id).join(',')}/`;
                    console.log('DEBUG: PDF Preview URL:', previewUrl);
                    return (
                      <object
                        data={previewUrl}
                        type="application/pdf"
                        className="w-full h-full rounded-lg"
                        title="Avanti Service Template"
                      >
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                          <p className="mb-2">PDF Preview Not Available</p>
                          <a 
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            Click here to view PDF
                          </a>
                        </div>
                      </object>
                    );
                  })()}
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleAvantiCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAvantiConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Downloading...
                      </div>
                    ) : (
                      'Confirm & Download'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full ${mode === 'return' ? 'bg-green-100' : 'bg-yellow-100'} sm:mx-0 sm:h-10 sm:w-10`}>
              <Wrench className={`h-6 w-6 ${mode === 'return' ? 'text-green-600' : 'text-yellow-600'}`} />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                {mode === 'return' ? 'Return Equipment from Service' : 'Send Equipment to Service'}
              </h3>
              {equipmentItems.length > 0 && (
                <div className="mt-2">
                  {equipmentItems.length === 1 ? (
                    <p className="text-sm text-gray-500">
                      Equipment: <span className="font-medium">{equipmentItems[0].safety_equipment_type_name}</span>
                      {equipmentItems[0].safety_equipment_serial_number && (
                        <span> (S/N: {equipmentItems[0].safety_equipment_serial_number})</span>
                      )}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500 font-medium">
                        {equipmentItems.length} equipment items selected:
                      </p>
                      <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-md p-2">
                        {equipmentItems.map((item, index) => (
                          <div key={item.id} className="text-xs text-gray-600">
                            {index + 1}. {item.safety_equipment_type_name}
                            {item.safety_equipment_serial_number && (
                              <span className="font-mono"> (S/N: {item.safety_equipment_serial_number})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                {mode === 'return' ? (
                  // Return mode: show current service data as readonly
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                      <p className="text-sm text-blue-800 font-medium mb-2">
                        Current Service Information:
                      </p>
                      <div className="space-y-1 text-sm text-gray-700">
                        <p>
                          <span className="font-medium">Service Company:</span>{' '}
                          {equipmentItems[0]?.service_company_name || 'N/A'}
                        </p>
                        <p>
                          <span className="font-medium">Service Date:</span>{' '}
                          {equipmentItems[0]?.service_date || 'N/A'}
                        </p>
                        {equipmentItems[0]?.service_notes && (
                          <p>
                            <span className="font-medium">Notes:</span>{' '}
                            {equipmentItems[0].service_notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Confirm to mark this equipment as returned from service and clear service data.
                    </p>
                  </>
                ) : (
                  // Send mode: editable fields
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Company *
                      </label>
                      <select
                        value={selectedCompany}
                        onChange={(e) => setSelectedCompany(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select service company...</option>
                        {serviceCompanies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                      {serviceCompanies.length === 0 && (
                        <p className="text-sm text-red-500 mt-1">
                          No service companies configured. Please add them in PPE Settings.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Date
                      </label>
                      <input
                        type="date"
                        value={serviceDate}
                        onChange={(e) => setServiceDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Additional notes about the service..."
                      />
                    </div>
                  </>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                      mode === 'return'
                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                        : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                    }`}
                    disabled={loading || (mode !== 'return' && !selectedCompany)}
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {mode === 'return' ? 'Processing...' : 'Sending...'}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Save className="h-4 w-4 mr-2" />
                        {mode === 'return' ? 'Confirm Return' : 'Send to Service'}
                      </div>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceModal;